import type { ComponentProps } from 'react'
import { useShallow } from 'zustand/shallow'

import InfoIcon from '@/components/atoms/icons/InfoIcon'
import VerticalDivider from '@/components/atoms/VerticalDivider'
import { useScopedI18n } from '@/locales/client'
import type { Part } from '@/services/schemas'
import useDialogsStore from '@/stores/DialogsStore'
import { cn } from '@/util/cn'
import { priceFormatter } from '@/util/formatter'

type PartHeaderProps = {
  part: Part | { partname: string, id?: string, pricing?: { retailprice: string, currency: 'USD' }, part_limit?: number }
  hideInfoAndPricing?: boolean
} & Omit<ComponentProps<'div'>, 'part'>

function PartHeader({ part, hideInfoAndPricing = false, className, ...props }: PartHeaderProps) {
  const t = useScopedI18n('partHeader')
  const toggle = useDialogsStore(useShallow(state => state.toggle))
  const setPartInfo = useDialogsStore(state => state.setPartInfo)

  const handleInfoClick = () => {
    toggle('partInfo')
    if (!part.id) return
    setPartInfo(part as Part)
  }

  return (
    <div
      className={cn(
        'col-span-2 flex h-[70px] flex-row items-center justify-between gap-1 rounded-lg bg-rsf-white p-2 md:p-4 pr-2 md:pr-4 md:gap-2',
        className,
      )}
      {...props}
    >
      <div className="flex flex-col">
        <p className="mb-1 text-balance break-words text-xs font-semibold leading-[14px] text-rsf-black sm:text-sm md:text-base lg:m-0 lg:text-base lg:leading-tight">
          {part.partname}
        </p>
        {!hideInfoAndPricing && (
          <p className="text-left text-xs font-normal tabular-nums leading-snug text-rsf-grey md:text-sm lg:hidden lg:text-base">
            {part.pricing?.retailprice ? priceFormatter(part.pricing.retailprice, part.pricing.currency) : t('free')}
          </p>
        )}
      </div>
      {!hideInfoAndPricing && (
        <div className="flex items-center">
          <button type="button" onClick={handleInfoClick}>
            <InfoIcon className="text-rsf-grey" />
          </button>
          <VerticalDivider className="hidden lg:block" />
          <p className="hidden w-[8ch] text-center text-sm font-normal tabular-nums text-rsf-grey md:text-base lg:block">
            {part.pricing?.retailprice ? priceFormatter(part.pricing.retailprice, part.pricing.currency) : t('free')}
          </p>
        </div>
      )}
    </div>
  )
}

export default PartHeader
